/**
 * Mirrors RW-ERP-Service/src/types/common/report-bug-constants.ts and
 * the emulated-user roster from src/types/draftProducts/constant.ts.
 *
 * The Asana customField IDs and project IDs MUST stay in sync with the
 * Vue ERP — they identify the same Asana destinations on both apps.
 */

export interface SelectOption {
  label: string;
  value: string;
}

export const PRIORITY_OPTIONS: ReadonlyArray<SelectOption> = [
  { label: 'Very High', value: '1206100737650861' },
  { label: 'High', value: '1206100737650859' },
  { label: 'Medium', value: '1206100737650860' },
  { label: 'Low', value: '1206101696335221' },
  { label: 'Very Low', value: '1206101696335222' },
  { label: 'Urgent', value: '1209749908099884' },
];

export const ISSUE_TYPE_OPTIONS: ReadonlyArray<SelectOption> = [
  { label: 'Bug', value: '1209749790719289' },
  { label: 'Query', value: '1209749790719290' },
  { label: 'Suggestion', value: '1209749790719291' },
  { label: 'Request', value: '1210497758244891' },
  { label: 'Others', value: '1209749790719292' },
];

export const DOMAIN_OPTIONS: ReadonlyArray<SelectOption> = [
  { label: 'Products', value: '1209767744166252' },
  { label: 'Orders', value: '1209767744166253' },
  { label: 'Material Combination', value: '1209767744166254' },
  { label: 'PO', value: '1209767744523073' },
  { label: 'Container', value: '1209767744523074' },
];

export const TEAM_OPTIONS: ReadonlyArray<SelectOption> = [
  { label: 'Design', value: '1208606476200336' },
  { label: 'Content', value: '1208606476200337' },
  { label: 'ERP', value: '1208606476200338' },
  { label: 'Data', value: '1208606476200339' },
  { label: 'Shopify', value: '1208606476200340' },
  { label: 'QA', value: '1208606476200341' },
  { label: 'Customizable', value: '1208606476200342' },
  { label: 'HR', value: '1208606476200343' },
  { label: 'Accounts', value: '1208606476200344' },
  { label: 'BPA', value: '1208606476200345' },
  { label: 'Marketing', value: '1208606478173885' },
  { label: 'Customer Service', value: '1208910179528471' },
  { label: 'Sourcing', value: '1208910179528472' },
  { label: 'Purchasing', value: '1208910179528473' },
];

export const SUBMIT_TICKET = {
  DEFAULT_TASK_PRIORITY: '1206100737650860', // Medium
  DEFAULT_ISSUE_TYPE: '1209749790719292', // Others
  DEFAULT_TEAM: '1208606476200338', // ERP
  QA_PROJECT_ID: '1206634216134139',
  PROD_PROJECT_ID: '1209749790719285',
  ERROR_LOG_PROJECT_ID: '1211912816387782',
  /** Asana custom field IDs, mirrored from Vue's submitToAsana(). */
  CUSTOM_FIELD_PRIORITY: '1206100737650858',
  CUSTOM_FIELD_TYPE: '1209749790719288',
  CUSTOM_FIELD_DOMAIN: '1209767744166251',
  CUSTOM_FIELD_TEAM: '1208606476200335',
  /** Label prefixed to the uploaded screenshot's URL in the ticket description. */
  SCREENSHOT_LINK_LABEL: 'Screenshot available here',
} as const;

/**
 * Header used to mark an outgoing request as exempt from the automatic
 * error-analytics reporter (`utils/bugLogger.ts`) — set on the Asana
 * "create task" request itself (`api/reportBug.api.ts`) so a failure
 * reporting a failure can never recurse. Shared constant so both the
 * setter and the reader reference the same string (no magic strings).
 */
export const SKIP_ANALYTICS_HEADER = 'x-skip-analytics' as const;

/**
 * Internal-staff email domain gate for the automatic error-analytics
 * reporter — only failures hit by a signed-in `@restaurantware.com` user
 * are auto-filed to Asana, never customer/vendor traffic. Mirrors
 * `RW-ERP-Service/src/utils/bugLogger.ts`'s hardcoded check, constantized
 * here per this repo's no-magic-strings convention.
 */
export const AUTO_REPORT_EMAIL_DOMAIN = '@restaurantware.com' as const;

/**
 * Operation permission ID required to use the Emulation feature.
 * Source: RW-ERP-Service/src/types/draftProducts/constant.ts → OPERATION_ID.
 */
export const EMULATION_OPERATION_ID = '2cdd12f6-13f6-47e4-8a94-87f225b7fcd2';

/** Frequent emulated users — same roster as Vue's `emulatedUserDetails`. */
export const FREQUENT_EMULATED_USERS: ReadonlyArray<SelectOption> = [
  { label: 'tech@rw.team', value: 'tech@rw.team' },
  { label: 'berlinda@restaurantware.com', value: 'berlinda@restaurantware.com' },
  { label: 'emily@restaurantware.com', value: 'emily@restaurantware.com' },
  { label: 'jamil@restaurantware.com', value: 'jamil@restaurantware.com' },
  { label: 'justin@restaurantware.com', value: 'justin@restaurantware.com' },
  { label: 'praveen@restaurantware.com', value: 'praveen@restaurantware.com' },
  { label: 'steve@restaurantware.com', value: 'steve@restaurantware.com' },
];
